<script setup lang="ts">
import type { IconName } from '@ui/components/icon/types'
import type { ButtonSizes } from '@ui/styles/Styles'
import type { NuxtLinkProps } from 'nuxt/app'
import Icon from '@ui/components/icon/Icon.vue'
import { ButtonVariant, Sizes } from '@ui/styles/Styles'
import { resolveComponent } from 'vue'
import Loader from '../loader/ButtonLoader.vue'

interface Props {
  name?: IconName
  variant?: ButtonVariant
  size?: ButtonSizes
  disabled?: boolean
  loading?: boolean
  to?: NuxtLinkProps['to']
  target?: string
  label?: string
}

withDefaults(defineProps<Props>(), {
  name: '',
  variant: ButtonVariant.Accent,
  size: Sizes.Luke,
  disabled: false,
  loading: false,
})

const NuxtLink = resolveComponent('NuxtLink')
</script>

<template lang="pug">
component.ui-icon-button.flex-shrink-0(
  :is="to ? NuxtLink : 'button'"
  type="button"
  :to="to"
  :target="target"
  :class="[size, variant, loading && 'loading', disabled && 'disabled']"
  :disabled="disabled || loading"
  :aria-label="label ? $t(label) : undefined"
)
  Icon.icon(v-if="name" :name="name")
  Loader.loader(v-if="loading")
</template>

<style scoped>
.ui-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  cursor: pointer;
  border: 1px solid;
  position: relative;
  background: transparent;
  text-decoration: none;
  transition: 0.2s ease;
  transition-property: color, background-color, border-color, box-shadow, opacity;

  --icon-button-content: var(--cue-content-neutral-primary);
  --icon-button-bg: transparent;
  --icon-button-border: transparent;
  --icon-button-loader: var(--icon-button-content);
  --icon-button-focus-inner: var(--cue-bg-surface-primary);
  --icon-button-focus-outer: var(--cue-border-primary-soft);
  --button-loader-dot-size: 6px;
  --button-loader-ring-size: 14px;

  color: var(--icon-button-content);
  background: var(--icon-button-bg);
  border-color: var(--icon-button-border);

  .icon {
    display: inline-flex;
    flex-shrink: 0;
    color: var(--icon-button-content);
  }

  &.r2d2 {
    --uno: s-28 br-10;
    --button-loader-ring-size: 12px;

    .icon {
      --uno: s-16;
    }
  }

  &.leia {
    --uno: s-36 br-12;

    .icon {
      --uno: s-20;
    }
  }

  &.hanSolo {
    --uno: s-40 br-14;

    .icon {
      --uno: s-20;
    }
  }

  &.luke {
    --uno: s-48 br-16;

    .icon {
      --uno: s-24;
    }
  }

  &.accent {
    --icon-button-bg: var(--cue-bg-accent-default);
    --icon-button-content: var(--cue-bg-accent-on-default);

    &:not(:disabled) {
      &:hover {
        --icon-button-bg: var(--cue-bg-accent-default-hover);
      }

      &:active {
        --icon-button-bg: var(--cue-bg-accent-default-pressed);
      }
    }

    &.disabled {
      --icon-button-content: var(--cue-content-neutral-disabled);
      --icon-button-bg: var(--cue-bg-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.primary {
    --icon-button-bg: var(--cue-bg-primary-default);
    --icon-button-content: var(--cue-bg-primary-on-default);

    &:not(:disabled) {
      &:hover {
        --icon-button-bg: var(--cue-bg-primary-default-hover);
      }

      &:active {
        --icon-button-bg: var(--cue-bg-primary-default-pressed);
      }
    }

    &.disabled {
      --icon-button-content: var(--cue-content-neutral-disabled);
      --icon-button-bg: var(--cue-bg-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.secondary {
    --icon-button-bg: var(--cue-bg-primary-soft-dark);
    --icon-button-content: var(--cue-content-primary-on-soft);

    &:not(:disabled) {
      &:hover {
        --icon-button-bg: var(--cue-bg-primary-soft-dark-hover);
      }

      &:active {
        --icon-button-bg: var(--cue-bg-primary-soft-dark-pressed);
      }
    }

    &.disabled {
      --icon-button-content: var(--cue-content-neutral-disabled);
      --icon-button-bg: var(--cue-bg-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.outline {
    --icon-button-bg: var(--cue-bg-neutral-none);
    --icon-button-content: var(--cue-content-primary-default);
    --icon-button-border: var(--cue-border-primary-default);

    &:not(:disabled) {
      &:hover {
        --icon-button-bg: var(--cue-bg-primary-soft-hover);
        --icon-button-content: var(--cue-content-primary-on-soft);
      }

      &:active {
        --icon-button-bg: var(--cue-bg-primary-soft-pressed);
        --icon-button-content: var(--cue-content-primary-on-soft);
      }
    }

    &.disabled {
      --icon-button-content: var(--cue-content-neutral-disabled);
      --icon-button-border: var(--cue-border-neutral-disabled);
      --icon-button-bg: var(--cue-bg-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.ghost {
    --icon-button-bg: var(--cue-bg-neutral-none);
    --icon-button-content: var(--cue-content-primary-default);

    &:not(:disabled) {
      &:hover {
        --icon-button-bg: var(--cue-bg-primary-soft-dark-hover);
        --icon-button-content: var(--cue-content-primary-on-soft);
      }

      &:active {
        --icon-button-bg: var(--cue-bg-primary-soft-dark-pressed);
        --icon-button-content: var(--cue-content-primary-on-soft);
      }
    }

    &.disabled {
      --icon-button-content: var(--cue-content-neutral-disabled);
      --icon-button-bg: var(--cue-bg-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.static {
    --icon-button-bg: var(--cue-static-neutral-main);
    --icon-button-content: var(--cue-static-primary-default);
    --icon-button-focus-inner: var(--cue-static-neutral-main);
    --icon-button-focus-outer: var(--cue-static-primary-soft-dark);

    &:not(:disabled) {
      &:hover .icon {
        opacity: 0.85;
      }

      &:active .icon {
        opacity: 0.7;
      }
    }

    &.disabled {
      --icon-button-content: var(--cue-content-neutral-disabled);
      --icon-button-bg: var(--cue-bg-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.function_accent {
    --icon-button-content: var(--cue-content-primary-default);

    &:not(:disabled) {
      &:hover {
        --icon-button-content: var(--cue-content-primary-hover);
      }

      &:active {
        --icon-button-content: var(--cue-content-primary-pressed);
      }
    }

    &.disabled {
      --icon-button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.function {
    --icon-button-content: var(--cue-content-neutral-primary);

    &:not(:disabled) {
      &:hover {
        --icon-button-content: var(--cue-content-neutral-secondary);
      }

      &:active {
        --icon-button-content: var(--cue-content-neutral-tertiary);
      }
    }

    &.disabled {
      --icon-button-content: var(--cue-content-neutral-disabled);
      cursor: not-allowed;
    }
  }

  &.function_accent,
  &.function {
    border: none;

    &.r2d2 {
      --uno: s-16;
    }

    &.leia {
      --uno: s-20;
    }

    &.hanSolo {
      --uno: s-20;
    }

    &.luke {
      --uno: s-24;
    }
  }

  &:not(:disabled):focus-visible {
    box-shadow:
      0 0 0 2px var(--icon-button-focus-inner),
      0 0 0 6px var(--icon-button-focus-outer);
  }

  &.loading {
    pointer-events: none;

    > *:not(.loader) {
      opacity: 0 !important;
    }
  }

  .loader {
    --uno: position-absolute l-0 t-0 w-100p h-100p;
    color: var(--icon-button-loader);
  }

  &:disabled {
    cursor: not-allowed;
  }
}
</style>
